import React, { useState } from 'react';
import { FluentProvider, Button, webDarkTheme, webLightTheme } from '@fluentui/react-components';
import { WeatherMoon24Regular, WeatherSunny24Regular } from '@fluentui/react-icons';
import toast, { Toaster } from 'react-hot-toast';
import EventList from './components/EventList';
import EffectsDatabase from './components/EffectsDatabase';
import RiskAnalysis from './components/RiskAnalysis';
import UserManagement from './components/UserManagement';
import SettingsModal from './components/SettingsModal';
import NewSetlistView from './components/NewSetlistView';
import SimpleInventoryView from './components/SimpleInventoryView';
import EventModal from './components/EventModal';
import { eventsAPI } from './services/api';
import { AuthProvider, useAuth, LoginScreen } from './components/GoogleAuth';
import NutChallenge from './components/NutChallenge';
import WorkoutTracker from './components/WorkoutTracker';
import SharedClipboard from './components/SharedClipboard';
import './App.css';

function AppContent() {
  const [currentView, setCurrentView] = useState('events');
  const [currentEvent, setCurrentEvent] = useState(null);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showEventEditor, setShowEventEditor] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('pyro-theme');
    return saved ? saved === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const { user, loading, signOut } = useAuth();
  const isWorkoutRoute = window.location.pathname === '/workout' || window.location.pathname === '/workout/';

  if (window.location.pathname === '/nut' || window.location.pathname === '/nut/') {
    return <NutChallenge />;
  }

  const toggleTheme = () => {
    setIsDarkMode((current) => {
      const next = !current;
      localStorage.setItem('pyro-theme', next ? 'dark' : 'light');
      return next;
    });
  };

  const saveEventFromDetails = async (eventData) => {
    try {
      const response = await eventsAPI.update(currentEvent.id, eventData);
      setCurrentEvent(response.data);
      setShowEventEditor(false);
      toast.success('Event updated successfully');
    } catch (error) {
      toast.error('Failed to update event');
      console.error('Error updating event:', error);
    }
  };

  if (loading) {
    return <FluentProvider theme={isDarkMode ? webDarkTheme : webLightTheme}><div className="loading-screen">Loading</div></FluentProvider>;
  }

  if (!user) {
    return <FluentProvider theme={isDarkMode ? webDarkTheme : webLightTheme}><LoginScreen /></FluentProvider>;
  }

  // Friends invited to the workout tracker only never see the rest of the app.
  if (isWorkoutRoute || user.workout_only) {
    return (
      <FluentProvider theme={isDarkMode ? webDarkTheme : webLightTheme}>
        <div className={`workout-standalone ${isDarkMode ? 'theme-dark' : 'theme-light'}`}>
          <WorkoutTracker
            user={user}
            isDarkMode={isDarkMode}
            onToggleTheme={toggleTheme}
            onSignOut={signOut}
          />
          <Toaster position="top-right" />
        </div>
      </FluentProvider>
    );
  }


  const renderCurrentView = () => {
    switch (currentView) {
      case 'events':
        return (
          <EventList 
            onOpenRiskAnalysis={(event) => {
              setCurrentEvent(event);
              setCurrentView('risk-analysis');
            }}
            onOpenEffectsDatabase={() => setCurrentView('effects')}
          />
        );
      case 'effects':
        return <EffectsDatabase onBack={() => setCurrentView('events')} />;
      case 'setlists':
        return <NewSetlistView onBack={() => setCurrentView('events')} />;
      case 'inventory':
        return <SimpleInventoryView onBack={() => setCurrentView('events')} />;
      case 'clipboard':
        return <SharedClipboard />;
      case 'risk-templates':
        return <SettingsModal
          onClose={() => setCurrentView('events')}
          currentUser={user}
          fullPage
        />;
      case 'risk-analysis':
        return (
          <RiskAnalysis 
            event={currentEvent}
            onBack={() => setCurrentView('events')}
            onEditEvent={() => setShowEventEditor(true)}
          />
        );
      default:
        return <EventList />;
    }
  };

  return (
    <FluentProvider theme={isDarkMode ? webDarkTheme : webLightTheme}>
    <div className={`App ${isDarkMode ? 'theme-dark' : 'theme-light'}`}>
      <header className="app-header">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <h1>Event Management</h1>
          {/* Cache bust: 2025-07-25-16:00-NO-CANVAS */}
          <div className="header-user-info" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            gap: '15px', 
            paddingRight: '20px',
            flexShrink: 0,
            minWidth: '0'
          }}>
            <span style={{ color: '#adb5bd', fontSize: '14px' }}>
              Welcome, {user.name}
            </span>
            <div className="header-buttons">
              <Button appearance="subtle" icon={isDarkMode ? <WeatherSunny24Regular /> : <WeatherMoon24Regular />} 
                aria-label={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
                onClick={toggleTheme}
                className="theme-button"
              >
                {isDarkMode ? 'Light mode' : 'Dark mode'}
              </Button>
              {(user.email === 'san.eriksen@gmail.com' || user.email === 'admin@localhost' || process.env.NODE_ENV === 'development') && (
                <Button appearance="secondary"
                  className="btn btn-secondary" 
                  onClick={() => setShowSettings(true)}
                  style={{ padding: '6px 12px', fontSize: '12px' }}
                >
                  Settings
                </Button>
              )}
                <Button appearance="secondary"
                  className="btn btn-secondary" 
                onClick={signOut}
                style={{ padding: '6px 12px', fontSize: '12px' }}
              >
                Sign Out
              </Button>
            </div>
          </div>
        </div>
        <nav className="nav">
          <button 
            className={`nav-item ${currentView === 'events' ? 'active' : ''}`}
            onClick={() => setCurrentView('events')}
          >
            Events
          </button>
          <button 
            className={`nav-item ${currentView === 'effects' ? 'active' : ''}`}
            onClick={() => setCurrentView('effects')}
          >
            Effects Database
          </button>
          <button 
            className={`nav-item ${currentView === 'setlists' ? 'active' : ''}`}
            onClick={() => setCurrentView('setlists')}
          >
            Setlists
          </button>
          <button 
            className={`nav-item ${currentView === 'inventory' ? 'active' : ''}`}
            onClick={() => setCurrentView('inventory')}
          >
            Inventory
          </button>
          <button className={`nav-item ${currentView === 'clipboard' ? 'active' : ''}`} onClick={() => setCurrentView('clipboard')}>
            Clipboard
          </button>
          <button
            className={`nav-item ${currentView === 'risk-templates' ? 'active' : ''}`}
            onClick={() => setCurrentView('risk-templates')}
          >
            Risk Templates
          </button>
        </nav>
      </header>
      
      <main className="container">
        {renderCurrentView()}
      </main>
      
      {showUserManagement && (
        <UserManagement onClose={() => setShowUserManagement(false)} />
      )}
      
      {showSettings && (
        <SettingsModal 
          onClose={() => setShowSettings(false)} 
          currentUser={user}
        />
      )}

      {showEventEditor && currentEvent && (
        <EventModal
          event={currentEvent}
          onSave={saveEventFromDetails}
          onClose={() => setShowEventEditor(false)}
        />
      )}
      
      <Toaster position="top-right" />
    </div>
    </FluentProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
